import React, { useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'

/**
 * Modern Welcome Modal Feature List
 * All icons/images removed. Features reflect latest PRD and Login.tsx.
 */
const welcomeFeatures = [
  {
    color: "bg-terracotta-400 dark:bg-terracotta-500",
    title: "ChefCoins for Advanced Features",
    description: "ChefCoins let you access premium tools like Recipe Studio and more advanced AI features. New users get 100 free!"
  },
  {
    color: "bg-sage-500 dark:bg-sage-400",
    title: "Organize Any Recipe",
    description: "Paste text from any source. AI instantly creates a clean, structured recipe."
  },
  {
    color: "bg-terracotta-500 dark:bg-terracotta-400",
    title: "Create with Studio",
    description: "Use the 5-step Recipe Studio to generate chef-persona recipes tailored to your needs."
  },
  {
    color: "bg-warmth-500 dark:bg-warmth-400",
    title: "Generate Food Photography",
    description: "Create beautiful, realistic food images directly from your recipe."
  },
  {
    color: "bg-sage-600 dark:bg-sage-400",
    title: "Real-Time Recipe Modification",
    description: "Edit recipes live with natural language—AI updates ingredients, steps, and more."
  },
  {
    color: "bg-sage-500 dark:bg-sage-400",
    title: "Analyze Nutritional Profile",
    description: "Our AI scans your ingredients to show its status for dietary profiles like Vegan and Gluten-Free."
  },
  {
    color: "bg-stone-500 dark:bg-stone-400",
    title: "Export & Share",
    description: "Export recipes as PDF, JSON, or CSV. Share and manage your collection easily."
  }
];

interface WelcomeModalProps {
  isOpen: boolean
  onClose: () => void
}

export const WelcomeModal: React.FC<WelcomeModalProps> = ({ isOpen, onClose }) => {
  const [dontShowAgain, setDontShowAgain] = useState(false)

  const handleClose = () => {
    if (dontShowAgain) {
      localStorage.setItem('chezalex-welcome-dismissed', 'true')
    }
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4 animate-backdrop-in">
      <div className="relative w-full sm:max-w-xl max-h-[100dvh] sm:max-h-[85vh] bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden animate-slide-up-mobile sm:animate-none pb-[max(env(safe-area-inset-bottom),0px)]">
        {/* Close Button */}
        <button
          onClick={handleClose}
          aria-label="Close"
          className="absolute top-3 right-3 z-10 p-3 h-11 w-11 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors flex items-center justify-center"
        >
          <X className="w-5 h-5 text-slate-700" />
        </button>
        {/* Content */}
        <div className="overflow-y-auto max-h-[100dvh] sm:max-h-[85vh]">
          <div className="p-6 sm:p-6 lg:p-10 pt-16 sm:pt-6">
            {/* Header */}
            <div className="text-center mb-8">
              <img
                src="/Cheflab.png"
                alt="cheflab Logo"
                className="h-16 w-auto object-contain mx-auto mb-4"
              />
              <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-terracotta-600 via-terracotta-500 to-sage-500 bg-clip-text text-transparent mb-3">
                Welcome to ChefLab
              </h1>
              <p className="text-slate-700 text-lg max-w-xl mx-auto">
                The next-generation AI-powered recipe platform. Discover, create, and manage recipes with advanced AI, chef personas, and beautiful food photography.
              </p>
            </div>
            {/* Modern Feature List */}
            <div className="mb-10">
              <ul className="space-y-5">
                {welcomeFeatures.map((feature, _idx) => (
                  <li key={feature.title} className="flex items-start gap-4">
                    <span
                      className={`mt-1 inline-block h-4 w-4 rounded-full shadow ${feature.color} flex-shrink-0`}
                      aria-hidden="true"
                    ></span>
                    <div>
                      <span className={`font-semibold text-slate-800 ${feature.title === "Create with Studio" ? "text-purple-700" : ""}`}>
                        {feature.title}
                        {feature.title === "Create with Studio" && (
                          <span className="ml-2 inline-block px-2 py-0.5 rounded bg-purple-100 text-xs font-bold text-purple-700 align-middle">NEW</span>
                        )}
                      </span>
                      <div className="text-slate-600 text-sm mt-1">
                        {feature.description}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            {/* Footer */}
            <div className="space-y-4 pt-4 border-t border-slate-200">
              <div className="flex items-center justify-center">
                <label className="flex items-center space-x-2 text-sm text-slate-600 cursor-pointer min-h-[44px]">
                  <input
                    type="checkbox"
                    checked={dontShowAgain}
                    onChange={(e) => setDontShowAgain(e.target.checked)}
                    className="rounded border-slate-300 h-5 w-5"
                  />
                  <span>Don't show this again</span>
                </label>
              </div>
              <div className="flex justify-center">
                <Button
                  onClick={handleClose}
                  size="lg"
                  className="bg-terracotta-500 hover:bg-terracotta-600 text-white border-0 shadow-md hover:shadow-lg transition-all duration-300 px-8 min-h-[48px]"
                >
                  Get Started
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}